import Field from '@/components/common/Field/Field';
import * as S from './style';
import InputText from '@/components/common/Input/InputText/InputText';
import InputSelect from '@/components/common/Input/InputSelect/InputSelect';
import { degreeOptions, formattedCountryOptions } from '@/constants/option';
import AddIcon from '@/components/Icon/AddIcon';
import { useFieldArray, useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';

import { useSignUpStore } from '../SignUpContext/SignUpContext';
import CloseIcon from '@/components/Icon/CloseIcon';
import { useEffect } from 'react';
interface RegisterField {
	educations: { school: string; major: string; degree: string; year: string; country: string }[]; // "name"을 필드로 갖는 배열
}
const EducationInfoForm = () => {
	const { setStep, updateEducationInfo, educationInfo, setCurrentStep } = useSignUpStore();
	const userRegisterSchema = z.object({
		educations: z.array(
			z.object({
				school: z.string().min(1, '학교를를 입력해 주세요.'),
				major: z.string().min(1, '전공을을 입력해 주세요.'),
				degree: z.string().min(1, '학위를 선택해 주세요.'),
				year: z.string().min(1, '졸업년도를 입력해 주세요.'),
				country: z.string().min(1, '국가를 선택해 주세요.'),
			})
		),
	});
	const {
		register,
		control,
		watch,
		handleSubmit,
		setValue,
		formState: { errors },
	} = useForm<RegisterField>({
		resolver: zodResolver(userRegisterSchema),
		defaultValues: {
			educations: [{ school: '', major: '', degree: '', year: '', country: '대한민국' }], // 초기값을 배열로 설정
		},
	});

	const { fields, append, remove } = useFieldArray({
		control,
		name: 'educations', // ✅ 배열 필드명을 올바르게 사용
	});

	const onValidateData = (_: RegisterField) => {
		append({ school: '', major: '', degree: '', year: '', country: '대한민국' });
	};
	const onSubmit = (data: RegisterField) => {
		updateEducationInfo(data.educations);
		setStep('기타정보 입력');
		setCurrentStep('기타정보 입력');
	};
	const educations = watch('educations'); // 전체 educations 감시

	// 모든 입력 값이 다 채워졌는지 검사하는 함수
	const isAllFieldsFilled = educations.every((edu) => edu.school && edu.major && edu.degree && edu.year && edu.country);
	useEffect(() => {
		const hasInfo = educationInfo.some((edu) => edu.school || edu.major || edu.degree || edu.year || edu.country);

		if (hasInfo) {
			const newEducations = educationInfo.map((item) => ({
				school: item.school ?? '',
				major: item.major ?? '',
				degree: item.degree ?? '',
				year: item.year ?? '',
				country: item.country ?? '',
			}));
			// 첫 번째 데이터는 setValue로 직접 설정
			if (newEducations.length > 0) {
				setValue('educations', newEducations);
			}
		}
	}, [educationInfo, setValue]);
	return (
		<S.FormContainer onSubmit={handleSubmit(onSubmit)}>
			<S.Container>
				<S.Danger>*필수 입력 값</S.Danger>

				{fields.map((f, index) => (
					<S.FieldBox key={f.id}>
						<S.Title>
							학력정보 {index + 1 > 9 ? '' : '0'}
							{index + 1}
							{index + 1 > 1 && <CloseIcon onClose={() => remove(index)} />}
						</S.Title>
						<S.FlexRow>
							<Field title="*출신학교" error={errors.educations?.[index]?.school?.message ?? ''}>
								<InputText {...register(`educations.${index}.school`)} isError={!!errors.educations?.[index]?.school?.message} placeholder="학교 입력" />
							</Field>
							<Field title="*전공" error={errors.educations?.[index]?.major?.message ?? ''}>
								<InputText isError={!!errors.educations?.[index]?.major?.message} {...register(`educations.${index}.major`)} placeholder="전공 입력" />
							</Field>
						</S.FlexRow>
						<Field title="*학위" error={errors.educations?.[index]?.degree?.message ?? ''}>
							<S.SelectBox>
								<InputSelect
									defaultValue="선택"
									options={degreeOptions}
									selectedOption={watch(`educations.${index}.degree`)}
									setSelectedOption={(value) => setValue(`educations.${index}.degree`, value, { shouldValidate: true })}
								/>
							</S.SelectBox>
						</Field>
						<Field title="*졸업년도" error={errors.educations?.[index]?.year?.message ?? ''}>
							<S.SelectBox>
								<InputText
									isError={!!errors.educations?.[index]?.year?.message}
									input={(e) => {
										e.target.value = e.target.value.replace(/[^0-9]/g, ''); // 숫자만 허용
										setValue(`educations.${index}.year`, e.target.value, { shouldValidate: true });
									}}
									value={watch(`educations.${index}.year`)}
									maxLength={4}
									placeholder="졸업년도 입력"
								/>
							</S.SelectBox>
						</Field>
						<Field title="*국가" error={errors.educations?.[index]?.country?.message ?? ''}>
							<S.SelectBox>
								<InputSelect
									options={formattedCountryOptions}
									selectedOption={watch(`educations.${index}.country`)}
									setSelectedOption={(value) => setValue(`educations.${index}.country`, value, { shouldValidate: true })}
								/>
							</S.SelectBox>
						</Field>
					</S.FieldBox>
				))}
				<S.AddButton type="button" onClick={handleSubmit(onValidateData)}>
					<AddIcon />
					추가
				</S.AddButton>
			</S.Container>
			<S.NextButton type="submit" disabled={!isAllFieldsFilled}>
				다음
			</S.NextButton>
		</S.FormContainer>
	);
};
export default EducationInfoForm;
